import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import Topsection from "./Topsection";
import FloatingButton from "./FloatingButton";
import { Helmet } from "react-helmet";

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^\w ]+/g, "")
    .replace(/ +/g, "-");

const PREDEFINED_CATEGORIES = [
  "Technology",
  "AI & ML",
  "Education",
  "Business",
  "Health",
  "Lifestyle",
  "News",
  "Sports",
];

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    category: "",
    sort: "newest",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 9;

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${process.env.REACT_APP_API_BASE}/blogs`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setBlogs(data || []);
        setFilteredBlogs(data || []);
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  useEffect(() => {
    if (!blogs || blogs.length === 0) return;

    let result = [...blogs];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(
        (blog) =>
          blog.title?.toLowerCase().includes(searchTerm) ||
          blog.author?.toLowerCase().includes(searchTerm) ||
          (blog.keywords || []).some((keyword) =>
            keyword.toLowerCase().includes(searchTerm)
          )
      );
    }

    // Category filter
    if (filters.category) {
      result = result.filter(
        (blog) =>
          (blog.keywords || []).some((keyword) =>
            keyword.toLowerCase().includes(filters.category.toLowerCase())
          ) ||
          (blog.category &&
            blog.category.toLowerCase() === filters.category.toLowerCase())
      );
    }

    // Sort filter
    if (filters.sort === "newest") {
      result.sort((a, b) => new Date(b.date) - new Date(a.date));
    } else if (filters.sort === "oldest") {
      result.sort((a, b) => new Date(a.date) - new Date(b.date));
    }

    setFilteredBlogs(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters, blogs]);

  // Get current blogs for pagination
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (error) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-danger">Error loading blogs: {error}</div>
        <button
          className="btn btn-primary mt-3"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Fortune Cloud Technologies | Blogs</title>
        <meta
          name="description"
          content="Explore courses and training programs at Fortune Cloud Technologies. Learn with experts and get placed."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <Topsection />
      <Header />
      <div className="courseslinear">
        <div className="container">
          <div className="row">
            <div className="col-md-12 p-2">
              <h2 className="mb-3 myfont1 text-dark fw-bold pt-2">Our Blogs</h2>

              <div className="card mb-4 shadow-sm">
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label htmlFor="search" className="form-label">
                        Search
                      </label>
                      <input
                        type="text"
                        id="search"
                        name="search"
                        className="form-control"
                        placeholder="Search blogs by Title"
                        value={filters.search}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="category" className="form-label">
                        Category
                      </label>
                      <select
                        id="category"
                        name="category"
                        className="form-control"
                        value={filters.category}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Categories</option>
                        {PREDEFINED_CATEGORIES.map((category, index) => (
                          <option key={index} value={category}>
                            {category}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="sort" className="form-label">
                        Sort By
                      </label>
                      <select
                        id="sort"
                        name="sort"
                        className="form-control"
                        value={filters.sort}
                        onChange={handleFilterChange}
                      >
                        <option value="newest">Latest First</option>
                        <option value="oldest">Oldest First</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-12">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading blogs...</p>
                </div>
              ) : filteredBlogs.length === 0 ? (
                <div className="alert alert-info">
                  No blogs found matching your criteria.
                </div>
              ) : (
                <>
                  <div className="row">
                    {currentBlogs.map((blog, index) => (
                      <div className="col-lg-4 col-md-6 col-12 mb-4" key={index}>
                        <Link
                          to={`/blogdetails/${slugify(blog?.title || "")}`}
                          className="text-decoration-none"
                        >
                          <div className="card h-100 shadow-sm border-0 overflow-hidden">
                            <div className="ratio ratio-16x9">
                              <img
                                src={`${process.env.REACT_APP_API_BASE}/uploads/blog/${blog?.image}`}
                                alt={blog?.title || "Blog image"}
                                className="img-fluid object-fit-cover"
                                style={{ objectPosition: "center" }}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/placeholder-image.jpg";
                                }}
                              />
                            </div>
                            <div className="card-body d-flex flex-column">
                              <div className="d-flex flex-wrap gap-1 mb-2">
                                {(blog?.keywords || []).map((keyword, i) => (
                                  <span
                                    key={i}
                                    className="badge bg-primary bg-opacity-10 text-primary"
                                  >
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                              <h5 className="card-title text-dark">
                                {blog?.title || "Untitled Blog"}
                              </h5>
                              <div className="mt-auto">
                                <p className="text-muted small">
                                  <i className="bi bi-tags me-1"></i>
                                  {blog?.category ||
                                    blog?.keywords?.[0] ||
                                    "General"}
                                </p>
                                <div className="mt-auto d-flex justify-content-around">
                                  <p className="text-muted small mb-0">
                                    <i className="bi bi-calendar me-1"></i>
                                    {blog?.date || "Unknown date"}
                                  </p>
                                  <p className="text-muted small mb-0">
                                    <i className="bi bi-person me-1"></i>
                                    {blog?.author || "Unknown author"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {totalPages > 1 && (
                    <nav aria-label="Blog pagination" className="mt-4">
                      <ul className="pagination justify-content-center">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                          <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => paginate(number)}
                            >
                              {number}
                            </button>
                          </li>
                        ))}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              )}
            </div>
          </div>
          <br />
        </div>
      </div>
      <Footer />
      <FloatingButton />
    </div>
  );
};

export default BlogList;